import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import HotelSidebar from "../../components/HotelSidebar";

export default function HotelAddCharges() {
  const { id } = useParams(); // guest_id
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  /* =====================================================
     🔥 FETCH EXISTING EXTRA CHARGES
  ===================================================== */
  useEffect(() => {
    const fetchCharges = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/extra-charges/${id}`,
          { credentials: "include" }
        );

        const data = await res.json();

        if (data.status === "success" && data.charges.length > 0) {
          setRows(
            data.charges.map((c) => ({
              id: c.id,           // 🔥 DB id
              title: c.title,
              amount: c.amount,
            }))
          );
        } else {
          setRows([{ title: "", amount: "" }]);
        }
      } catch (err) {
        console.error(err);
        setRows([{ title: "", amount: "" }]);
      }
    };

    fetchCharges();
  }, [id]);

  /* =====================================================
     ADD / REMOVE / CHANGE
  ===================================================== */
  const addRow = () => {
    setRows([...rows, { title: "", amount: "" }]);
  };

  const handleChange = (index, field, value) => {
    const updated = [...rows];
    updated[index][field] = value;
    setRows(updated);
  };

  /* =====================================================
     🔥 DELETE (DB + UI)
  ===================================================== */
  const deleteRow = async (index) => {
    const row = rows[index];

    // agar DB ka data hai
    if (row.id) {
      if (!window.confirm("Delete this charge?")) return;

      await fetch(
        `${process.env.REACT_APP_API_URL}/api/extra-charges/${row.id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
    }

    const updated = rows.filter((_, i) => i !== index);
    setRows(updated.length ? updated : [{ title: "", amount: "" }]);
  };

  /* =====================================================
     TOTAL
  ===================================================== */
  const totalAmount = useMemo(() => {
    return rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
  }, [rows]);

  /* =====================================================
     SAVE NEW CHARGES ONLY
  ===================================================== */
  const saveCharges = async () => {
    const newCharges = rows.filter((r) => !r.id);

    if (newCharges.length === 0) {
      alert("No new charges to save");
      return;
    }

    for (let r of newCharges) {
      if (!r.title || Number(r.amount) <= 0) {
        alert("Fill all new rows correctly");
        return;
      }
    }

    setLoading(true);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/api/extra-charges/bulk`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            guest_id: Number(id),
            charges: newCharges,
          }),
        }
      );

      const data = await res.json();
      setLoading(false);

      if (data.status === "success") {
        navigate("/hotel/check-out");
      } else {
        alert("Failed to save");
      }
    } catch (err) {
      setLoading(false);
      alert("Server error");
    }
  };

  /* =====================================================
     UI
  ===================================================== */
  return (
    <div className="flex">
      <HotelSidebar />

      <div className="flex-1 ml-64 p-6 bg-amber-50 min-h-screen">
        <div className="max-w-2xl mx-auto bg-white p-6 rounded-xl shadow">
          <h1 className="text-xl font-bold mb-4">➕ Add Extra Charges</h1>

          <table className="w-full border mb-3">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2">Title</th>
                <th className="border p-2">Amount ₹</th>
                <th className="border p-2">Action</th>
              </tr>
            </thead>

            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td className="border p-2">
                    <input
                      className="border px-2 py-1 w-full rounded"
                      value={row.title}
                      placeholder="Coffee / Food"
                      onChange={(e) =>
                        handleChange(i, "title", e.target.value)
                      }
                    />
                  </td>

                  <td className="border p-2">
                    <input
                      type="number"
                      min="1"
                      className="border px-2 py-1 w-full rounded"
                      value={row.amount}
                      onChange={(e) =>
                        handleChange(i, "amount", e.target.value)
                      }
                    />
                  </td>

                  <td className="border p-2 text-center">
                    {i === rows.length - 1 ? (
                      <button
                        onClick={addRow}
                        className="bg-green-600 text-white px-3 py-1 rounded"
                      >
                        +
                      </button>
                    ) : (
                      <button
                        onClick={() => deleteRow(i)}
                        className="bg-red-600 text-white px-3 py-1 rounded"
                      >
                        −
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="text-right font-bold mb-4">
            Total Extra Charges: ₹{totalAmount}
          </div>

          <button
            onClick={saveCharges}
            disabled={loading}
            className="bg-blue-700 text-white px-6 py-2 rounded w-full disabled:opacity-50"
          >
            {loading ? "Saving..." : "Save New Charges"}
          </button>
        </div>
      </div>
    </div>
  );
}
